import "../Pages/Service.css";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
export function Service() {
  const heroRef = useRef(null);
  const pricingRef = useRef(null);
  const [selectedPlan, setSelectedPlan] = useState("standard"); // "standard" or "premium"
  const [openFAQ, setOpenFAQ] = useState(); // Track which FAQ is open (0 = first one)
  const navigate = useNavigate();

  // Smooth scroll to pricing section
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Handle free trial signup flow
  const handleStartFreeTrial = async (plan) => {
    setSelectedPlan(plan);

    // Check if user is logged in
    const token = localStorage.getItem("token");

    if (!token) {
      // Not logged in -> save plan choice and redirect to login
      localStorage.setItem("selectedPlan", plan);
      navigate("/login");
      return;
    }

    // User is logged in -> check if they have a company profile
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/company/get-company-profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        // Has company profile -> go to portal
        localStorage.removeItem("selectedPlan"); // Clean up
        navigate("/management");
      } else if (response.status === 404) {
        // No company profile found -> create one first
        localStorage.removeItem("selectedPlan"); // Clean up
        navigate("/new-company");
      } else if (response.status === 401) {
        // Token invalid or expired -> redirect to login
        localStorage.removeItem("token");
        localStorage.setItem("selectedPlan", plan);
        navigate("/login");
      } else {
        // Other errors -> redirect to login to be safe
        localStorage.setItem("selectedPlan", plan);
        navigate("/login");
      }
    } catch (error) {
      console.error("Error checking company profile:", error);
      // Network error -> redirect to login to be safe
      localStorage.setItem("selectedPlan", plan);
      navigate("/login");
    }
  };

  // Reveal animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("s2-show");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }
    if (pricingRef.current) {
      observer.observe(pricingRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="s2-page">
      {/* Hero Section */}
      <section className="s2-hero" ref={heroRef}>
        <div className="s2-container">
          {/* Badge */}
          <div className="s2-badge s2-animate" style={{ "--delay": "0ms" }}>
            <span className="s2-badge-icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
              </svg>
            </span>
            Month-to-Month. No Contracts. No Hidden Fees.
          </div>

          {/* Title */}
          <h1 className="s2-title s2-animate" style={{ "--delay": "100ms" }}>
            Affordable pricing <span className="s2-gradient-text">plans</span>
          </h1>

          {/* Subtitle */}
          <p className="s2-subtitle s2-animate" style={{ "--delay": "200ms" }}>
            Professional social media management that fits your budget. Start
            growing your brand today.
          </p>

          {/* CTAs */}
          <div
            className="s2-cta-group s2-animate"
            style={{ "--delay": "300ms" }}
          >
            <button
              className="s2-btn s2-btn-primary"
              type="button"
              onClick={() => handleStartFreeTrial("standard")}
            >
              Get Started
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12h14M12 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="s2-btn s2-btn-outline"
              type="button"
              onClick={scrollToPricing}
            >
              View Features
            </button>
          </div>

          {/* Benefits Grid */}
          <div className="s2-benefits" role="list">
            <div
              className="s2-benefit s2-animate"
              role="listitem"
              style={{ "--delay": "400ms" }}
            >
              <div
                className="s2-benefit-icon s2-icon-orange"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 2l7 3v6c0 5-3.5 9.5-7 11-3.5-1.5-7-6-7-11V5l7-3z" />
                </svg>
              </div>
              <h3 className="s2-benefit-title">30-Day Guarantee</h3>
              <p className="s2-benefit-text">Full refund if not satisfied</p>
            </div>

            <div
              className="s2-benefit s2-animate"
              role="listitem"
              style={{ "--delay": "500ms" }}
            >
              <div
                className="s2-benefit-icon s2-icon-purple"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5 0-9 2.5-9 5.5V22h18v-2.5C21 16.5 17 14 12 14z" />
                </svg>
              </div>
              <h3 className="s2-benefit-title">Expert Team</h3>
              <p className="s2-benefit-text">Professional content creators</p>
            </div>

            <div
              className="s2-benefit s2-animate"
              role="listitem"
              style={{ "--delay": "600ms" }}
            >
              <div
                className="s2-benefit-icon s2-icon-orange"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                </svg>
              </div>
              <h3 className="s2-benefit-title">Fast Delivery</h3>
              <p className="s2-benefit-text">Consistent weekly posting</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="s2-pricing" ref={pricingRef}>
        <div className="s2-container">
          {/* Section Header */}
          <div className="s2-pricing-header">
            <h2
              className="s2-pricing-title s2-animate"
              style={{ "--delay": "0ms" }}
            >
              Choose Your Plan
            </h2>
            <p
              className="s2-pricing-subtitle s2-animate"
              style={{ "--delay": "100ms" }}
            >
              Select the perfect plan for your business needs
            </p>
          </div>

          {/* Pricing Cards Grid */}
          <div className="s2-pricing-grid">
            {/* Standard Plan */}
            <article
              className={`s2-pricing-card s2-card-popular s2-animate`}
              style={{ "--delay": "200ms" }}
            >
              <div className="s2-popular-badge">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
                </svg>
                Most Popular
              </div>

              <div className="s2-card-header">
                <h3 className="s2-card-plan-name">Standard</h3>
                 
              </div>

              <div className="s2-card-price">
                <span className="s2-price-symbol">$</span>
                <span className="s2-price-amount">99</span>
                <span className="s2-price-term">/mo</span>
              </div>

              <ul className="s2-card-features" role="list">
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  8 Post Per Month
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  2 Videos Per Month
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  1 Email Campaign Per Month
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  2 Social Channels Included
                </li>{" "}
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Scheduling and Posting
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  One Platform for Unlimited Review
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  30 Minutes Weekly Call
                </li>{" "}
                <li className="s2-feature-item">
                
                </li>{" "}
                <li className="s2-feature-item">
                
                </li>
                <li className="s2-feature-item">
                
                </li>
                <li className="s2-feature-item">
                
                </li>
                <li className="s2-feature-item">
                
                </li>
              </ul>

              {/* <button
                className={`s2-card-btn ${
                  selectedPlan === "standard"
                    ? "s2-btn-selected"
                    : "s2-btn-choose"
                }`}
                type="button"
                onClick={() => setSelectedPlan("standard")}
              >
                {selectedPlan === "standard" ? "Selected" : "Choose Plan"}
              </button> */}
              <button
                className="s2-card-btn s2-btn-choose"
                type="button"
                onClick={() => handleStartFreeTrial("standard")}
              >
                Experience the 30-Day Free Trial
              </button>
            </article>

            {/* Premium Plan */}
            <article
              className="s2-pricing-card s2-animate"
              style={{ "--delay": "300ms" }}
            >
              <div className="s2-card-header">
                <h3 className="s2-card-plan-name">Premium</h3>
              
              </div>

              <div className="s2-card-price">
                <span className="s2-price-symbol">$</span>
                <span className="s2-price-amount">199</span>
                <span className="s2-price-term">/mo</span>
              </div>

              <ul className="s2-card-features" role="list">
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  16 Post Per Month
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  4 Videos Per Month
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  4 Email Campaign Per Month
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  4 Social Channels Included
                </li>{" "}
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Scheduling and Posting
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  One Platform for Unlimited Review
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  30 Minutes Weekly Call
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Simple Website Maintenance
                </li>
                <li className="s2-feature-item">
                  <svg
                    className="s2-check-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Monthly Customer Engagement Report
                </li>
              </ul>

              <button
                className="s2-card-btn s2-btn-choose"
                type="button"
                onClick={() => handleStartFreeTrial("premium")}
              >
                Experience the 30-Day Free Trial
              </button>
              {/* <button
                className={`s2-card-btn ${
                  selectedPlan === "premium"
                    ? "s2-btn-selected"
                    : "s2-btn-choose"
                }`}
                type="button"
                onClick={() => setSelectedPlan("premium")}
              >
                {selectedPlan === "premium" ? "Selected" : "Choose Plan"}
              </button> */}
            </article>
          </div>

          {/* Footer Text */}
          <p
            className="s2-pricing-footer s2-animate"
            style={{ "--delay": "400ms" }}
          >
            Pricing is in <span className="s2-usd">USD</span>. Auto-renews
            unless cancelled. By subscribing you agree to{" "}
            <a href="#terms" className="s2-footer-link">
              Terms & Conditions
            </a>
            . Cancellation requires at least 7 days notice. 30-day money back
            guarantee.
          </p>
        </div>
      </section>

      {/* Included Features Section */}
      <section className="s2-included">
        <div className="s2-container">
          {/* Section Header */}
          <div className="s2-included-header">
            <h2 className="s2-included-title">
              Included with <span className="s2-gradient-text">each plan</span>
            </h2>
            <p className="s2-included-subtitle">
              $99 offers social media content management to help busy
              entrepreneurs like you maintain a solid presence on social media.
            </p>
          </div>

          {/* Features Grid */}
          <div className="s2-included-grid">
            {/* Feature 1 */}
            <div className="s2-included-card">
              <div className="s2-included-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="s2-included-card-title">High-quality content</h3>
              <p className="s2-included-card-text">
                Elevate your brand's online presence with our high-quality
                content. Our team of creative professionals crafts compelling
                posts that resonate with your target audience.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="s2-included-card">
              <div className="s2-included-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM5 10h5v7H5zm6-3h8v2h-8zm0 4h8v2h-8zm0 4h5v2h-5z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="s2-included-card-title">Graphic design posts</h3>
              <p className="s2-included-card-text">
                Transform your social media feeds with our visually stunning
                graphic design posts. We combine striking visuals with impactful
                messages to drive engagement.
              </p>
            </div>

            {/* Feature 3 - Highlighted */}
            <div className="s2-included-card  ">
              <div className="s2-included-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="s2-included-card-title">
                Review & approval process
              </h3>
              <p className="s2-included-card-text">
                Every piece of content goes through our rigorous review and
                approval process, ensuring that only the best content represents
                your brand online.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="s2-included-card">
              <div className="s2-included-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="s2-included-card-title">Email Campaign Support</h3>
              <p className="s2-included-card-text">
                Extend your reach beyond social media with our integrated email
                campaign support. We help you maintain consistent messaging
                across all channels.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="s2-included-card">
              <div className="s2-included-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="s2-included-card-title">
                Consistent posting schedule
              </h3>
              <p className="s2-included-card-text">
                Maintain a solid presence on social media with our reliable
                posting schedule. Your customers will find you consistently
                online, building trust and credibility.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="s2-included-card">
              <div className="s2-included-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="s2-included-card-title">
                30-Day Money Back Guarantee
              </h3>
              <p className="s2-included-card-text">
                Try our service risk-free with our 30-day money back guarantee.
                If you're not satisfied with the quality, we'll provide a full
                refund.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Money Back Guarantee Section */}
      <section className="s2-guarantee">
        <div className="s2-container">
          <div className="s2-guarantee-content">
            {/* Icon */}
            <div className="s2-guarantee-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Title */}
            <h2 className="s2-guarantee-title">30-Day Money Back Guarantee</h2>

            {/* Description */}
            <p className="s2-guarantee-desc">
              If you are not satisfied with the quality of your social media
              posts you can cancel and get a full refund within the first 30
              days of your subscription letting you try 99 Dollar Social with no
              risk on your end.
            </p>

            {/* Features Grid */}
            <div className="s2-guarantee-features">
              <div className="s2-guarantee-feature">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <h4 className="s2-guarantee-feature-title">
                    No Questions Asked
                  </h4>
                  <p className="s2-guarantee-feature-text">
                    Simple refund process
                  </p>
                </div>
              </div>

              <div className="s2-guarantee-feature">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <h4 className="s2-guarantee-feature-title">Full Refund</h4>
                  <p className="s2-guarantee-feature-text">100% money back</p>
                </div>
              </div>

              <div className="s2-guarantee-feature">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <h4 className="s2-guarantee-feature-title">30 Days</h4>
                  <p className="s2-guarantee-feature-text">
                    Plenty of time to decide
                  </p>
                </div>
              </div>

              <div className="s2-guarantee-feature">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <h4 className="s2-guarantee-feature-title">Risk-Free</h4>
                  <p className="s2-guarantee-feature-text">
                    Try with confidence
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              className="s2-guarantee-btn"
              type="button"
              onClick={() => handleStartFreeTrial("standard")}
            >
              Start Your Risk-Free Trial
            </button>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="s2-faq">
        <div className="s2-container">
          {/* Section Header */}
          <div className="s2-faq-header">
            <h2 className="s2-faq-title">Frequently Asked Questions</h2>
            <p className="s2-faq-subtitle">
              Everything you need to know about our service
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="s2-faq-list">
            <FAQItem
              question="Can I cancel anytime?"
              answer="Yes! We offer month-to-month subscriptions with no long-term contracts. You can cancel at any time with at least 7 days notice."
              isOpen={openFAQ === 0}
              onToggle={() => setOpenFAQ(openFAQ === 0 ? -1 : 0)}
            />
            <FAQItem
              question="What social media platforms do you support?"
              answer="We support all major social media platforms including Facebook, Instagram, Twitter, LinkedIn, TikTok, and Pinterest. You can choose which platforms you want us to manage based on your business needs."
              isOpen={openFAQ === 1}
              onToggle={() => setOpenFAQ(openFAQ === 1 ? -1 : 1)}
            />
            <FAQItem
              question="How does the approval process work?"
              answer="Every piece of content goes through our rigorous review and approval process. You'll receive drafts of your posts for review, and you can request revisions before they go live. We ensure only the best content represents your brand."
              isOpen={openFAQ === 2}
              onToggle={() => setOpenFAQ(openFAQ === 2 ? -1 : 2)}
            />
            <FAQItem
              question="What if I need more posts than my plan includes?"
              answer="You can easily upgrade to a higher plan or purchase additional posts à la carte. Contact our team to discuss custom packages that fit your specific needs."
              isOpen={openFAQ === 3}
              onToggle={() => setOpenFAQ(openFAQ === 3 ? -1 : 3)}
            />
            <FAQItem
              question="Do you provide content for my specific industry?"
              answer="Absolutely! Our team of creative professionals has experience across various industries. We tailor content to match your industry standards, audience expectations, and brand voice."
              isOpen={openFAQ === 4}
              onToggle={() => setOpenFAQ(openFAQ === 4 ? -1 : 4)}
            />
            <FAQItem
              question="How quickly can I get started?"
              answer="You can get started immediately after signing up! We'll schedule an onboarding call within 24 hours to understand your brand, and you'll receive your first content drafts within 3-5 business days."
              isOpen={openFAQ === 5}
              onToggle={() => setOpenFAQ(openFAQ === 5 ? -1 : 5)}
            />
          </div>
        </div>
      </section>
      {/* Final CTA Section */}
      <section className="s2-final-cta">
        <div className="s2-container">
          <div className="s2-final-cta-content">
            {/* Badge */}
            <div className="s2-final-badge">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
              </svg>
              Limited Time Offer
            </div>

            {/* Title */}
            <h2 className="s2-final-title">
              Ready to grow your{" "}
              <span className="s2-final-gradient-social">social</span>
              <br />
              <span className="s2-final-gradient-media">media presence</span>?
            </h2>

            {/* Description */}
            <p className="s2-final-desc">
              Join hundreds of entrepreneurs who trust us with their social
              media. Start your risk-free trial today.
            </p>

            {/* CTA Buttons */}
            <div className="s2-final-buttons">
              <button
                className="s2-final-btn s2-final-btn-primary"
                type="button"
                onClick={() => handleStartFreeTrial("standard")}
              >
                Get Started Now
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <Link to="/book-demo">
                <button
                  className="s2-final-btn s2-final-btn-secondary"
                  type="button"  style={{ cursor: "pointer" }}
                >
                  Schedule a Demo
                </button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <p className="s2-final-trust">
              No credit card required • Cancel anytime • 30-day money back
              guarantee
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className={`s2-faq-item ${isOpen ? "s2-faq-item-open" : ""}`}>
      <button
        className="s2-faq-question"
        onClick={onToggle}
        type="button"
        aria-expanded={isOpen}
      >
        <span className="s2-faq-question-text">{question}</span>
        <svg
          className="s2-faq-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="s2-faq-answer">
        <div className="s2-faq-answer-content">
          <p>{answer}</p>
        </div>
      </div>
    </div>
  );
}
